import { Editor } from "@monaco-editor/react";
import yaml from "js-yaml";
import json5 from "json5";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SplitPane from "react-split-pane";
import Pane from "react-split-pane/lib/Pane";
import { requestValidation } from "../../../abstract/api/rest";
import { OutletTypes } from "../../../abstract/controller/enum/OutletTypes";
import { exportToYaml } from "../../../abstract/controller/modelYamlExport";
import { importFromYaml } from "../../../abstract/controller/modelYamlImport";
import * as historyUtils from "../../../abstract/controller/utils/historyUtils";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import * as pathUtils from "../../../abstract/controller/utils/pathUtils";
import * as systemUtils from "../../../abstract/controller/utils/systemUtils";
import {
  getPaneAsideCL,
  getPaneAsideCLR,
  getPaneAsideCR,
  getPaneBodyFC,
  getPaneBodyGF,
  getPaneBodyGFC,
  setPaneAsideCL,
  setPaneAsideCLR,
  setPaneAsideCR,
  setPaneBodyFC,
  setPaneBodyGF,
  setPaneBodyGFC,
} from "../../../abstract/db/pane";
import { getShowPageGenerator } from "../../../abstract/redux/slice/generatorSlice";
import { setValidation } from "../../../abstract/redux/slice/metaSlice";
import { getOutletType, setOutlet } from "../../../abstract/redux/slice/outletSlice";
import {
  getIsAnyContextMenuDisplayed,
  getIsAnyHighlighted,
  getSpinner,
  getSpinnerMessage,
  setContextMenu,
  setHighlight,
  setSpinner,
} from "../../../abstract/redux/slice/overlaySlice";
import {
  editRoot,
  getIdFunction,
  getNodeStructure,
  getRoot,
  setRoot,
} from "../../../abstract/redux/slice/structureSlice";
import DataGenerationPage from "../../../pages/generator/DataGenerationPage";
import { ToastContext } from "../../ui/pop-ups/Toast";
import Spinner from "../../ui/spinner/Spinner";
import { AiFunctionHelper, useSyncMonacoWithTextContent } from "./ai/AiFunction";
import { YamlDiffViewer } from "./ai/AiYamlDiffViewer";
import AsideLeft from "./AsideLeft";
import AsideRight from "./AsideRight";
import BodyViewConditions from "./BodyViewConditions";
import BodyViewFunction from "./BodyViewFunction";
import BodyViewGlobalParameters from "./BodyViewGlobalParameters";
import CanvasFooterTrash from "./FooterTrash";
import styles from "./Panel.module.scss";
import { getEndpointTomato } from "../../../abstract/redux/slice/endpointSlice";

const initialModelYaml = "./example/initial.yaml";

export default function Panel() {
  const dispatch = useDispatch();

  const { addToast } = useContext(ToastContext);

  const _endpointTomato = useSelector(getEndpointTomato);

  const _spinner = useSelector(getSpinner);
  const _outletType = useSelector(getOutletType);
  const _spinnerMessage = useSelector(getSpinnerMessage);

  const _nodeRoot = useSelector(getRoot);
  const _idFunction = useSelector(getIdFunction);
  const _nodeFunction = useSelector(getNodeStructure(_idFunction));

  const _showPageGenerator = useSelector(getShowPageGenerator);

  const [_showAsideLeft, _setShowAsideLeft] = useState(0);
  const [_showAsideRight, _setShowAsideRight] = useState(-1);

  const [_showGlobals, _setShowGlobals] = useState(false);
  const [_showFunction, _setShowFunction] = useState(true);
  const [_showConditions, _setShowConditions] = useState(false);

  const [textContent, setTextContent] = useState("");
  const textContentSnapshot = useRef("");

  const _viewRefPanel = useRef();

  const _functions = nodeUtils.getFunctions(_nodeRoot);

  const _isGUI = _outletType === OutletTypes.GUI;

  const [aiYamlHistory, setAiYamlHistory] = useState([]);
  const [yamlDiff, setYamlDiff] = useState([]);

  const currentModelYaml = _nodeRoot ? exportToYaml(_nodeRoot) : "";

  const enhancedHandleAiResponse = (aiMessage) => {
    dispatch(setOutlet(OutletTypes.EDITOR));

    let isYaml = false;
    try {
      const parsed = yaml.load(aiMessage);
      isYaml = parsed && typeof parsed === "object";
    } catch (e) {
      isYaml = false;
    }

    if (isYaml) {
      AiFunctionHelper.handleAiResponse(
        aiMessage,
        currentModelYaml,
        setTextContent,
        setYamlDiff,
        setAiYamlHistory
      );
    }
  };

  const enhancedHandleApprove = () => {
    const originalHandleApprove = AiFunctionHelper.handleApprove({
      aiYamlHistory,
      importFromYaml,
      dispatch,
      editRoot,
      setTextContent,
      setYamlDiff,
      setOutlet,
      OutletTypes,
      customDescription: "Modify by assistant.",
    });

    const result = originalHandleApprove();
    return result;
  };

  const enhancedHandleReject = AiFunctionHelper.handleReject({
    setYamlDiff,
    currentModelYaml,
    setTextContent,
  });

  useEffect(() => {
    if (_nodeRoot && yamlDiff.length === 0) {
      const yamlContent = exportToYaml(_nodeRoot);
      if (!textContent || textContent === "") {
        setTextContent(yamlContent);
        textContentSnapshot.current = yamlContent;
      }
    }
  }, [_nodeRoot, textContent, yamlDiff.length]);

  const yamlContent = exportToYaml(_nodeRoot);

  textContentSnapshot.current = yamlContent;

  useEffect(() => {
    if (Object.keys(_nodeRoot).length !== 0) {
      if (
        nodeUtils
          .getFunctions(_nodeRoot)
          .map((e) => nodeUtils.getID(e))
          .some((e) => e === _idFunction)
      ) {
        return;
      }
    }

    const modelCandidate = historyUtils.loadSessionModel();

    if (modelCandidate) {
      let modelParsed;

      try {
        modelParsed = JSON.parse(modelCandidate);
      } catch (e) {
        addToast(
          'Data corrupted! Reversed to the default model. Current model saved under "backup-model" in session storage.',
          "error"
        );
        historyUtils.saveBackupSessionModel(modelCandidate);
        modelParsed = JSON.parse(
          '{"functions":[{"type":"function","name":"Default","id":"root→[function]→Default","uuid":"3050e9e6-8c49-40e7-21fb-2c9488f8e863","timeCreate":0,"timeUpdate":1760608375002}],"id":"root","name":"root","type":"root","uuid":"836c107d-f01a-4bbc-2737-f89e64d0a767","timeCreate":0,"timeUpdate":1760608375002}'
        );
      }

      dispatch(setRoot(modelParsed, 0));
      dispatch(setSpinner(false));
    } else {
      fetch(initialModelYaml).then(async (e) => {
        const _content = await e.text();
        const { model: _results } = importFromYaml(_content);
        dispatch(setRoot(_results, 0));
        dispatch(setSpinner(false));
      });
    }
  }, [_idFunction, _nodeRoot, addToast, dispatch]);

  const _handleShowGlobals = (show) => {
    _setShowGlobals(show);

    if (!show) {
      _setShowFunction(true);
    }
  };

  const _handleShowFunction = (show) => {
    _setShowFunction(show);
    _setShowConditions(false);

    if (!show) {
      _setShowGlobals(true);
    }
  };

  const _handleShowConditions = (show) => {
    _setShowConditions(show);
  };

  const _handleSwitchToGui = () => {
    if (_isGUI) {
      return;
    }

    if (textContent === textContentSnapshot.current) {
      dispatch(setOutlet(OutletTypes.GUI));
      systemUtils.removeFromSessionStorage("finalYaml");
      return;
    }

    const { model: _model, error: _error } = importFromYaml(textContent);

    if (_model) {
      dispatch(editRoot(_model, _idFunction));
      dispatch(setOutlet(OutletTypes.GUI));
      textContentSnapshot.current = textContent;
    } else if (_error) {
      alert(_error);
    }
  };

  const _handleSwitchToText = () => {
    if (!_isGUI) {
      return;
    }

    textContentSnapshot.current = exportToYaml(_nodeRoot);

    setTextContent(textContentSnapshot.current);
    dispatch(setOutlet(OutletTypes.EDITOR));
  };

  const refGlobals = (
    <BodyViewGlobalParameters
      nodeRoot={_nodeRoot}
      showGlobals={_showGlobals}
      showFunction={_showFunction}
      showConditions={_showConditions}
      setShowGlobals={_setShowGlobals}
      setShowFunction={_setShowFunction}
    />
  );

  const refFunction = (
    <BodyViewFunction
      nodeFunction={_nodeFunction}
      showFunction={_showFunction}
      setShowGlobals={_setShowGlobals}
      setShowFunction={_setShowFunction}
    />
  );

  const refConditions = (
    <BodyViewConditions
      nodeFunction={_nodeFunction}
      showFunction={_showFunction}
      showConditions={_showConditions}
      setShowConditions={_setShowConditions}
    />
  );

  const refContainerBody = (
    <div className={styles["body"]}>
      <BodyHeader
        isGui={_isGUI}
        handleSwitchToGui={_handleSwitchToGui}
        handleSwitchToText={_handleSwitchToText}
      />
      {_isGUI ? (
        <BodyContainerGUI
          functions={_functions}
          refGlobals={refGlobals}
          refFunctions={refFunction}
          refConditions={refConditions}
          showGlobals={_showGlobals}
          showFunction={_showFunction}
          showConditions={_showConditions}
          viewRef={_viewRefPanel}
        />
      ) : yamlDiff.length > 0 ? (
        <YamlDiffViewer
          diffLines={yamlDiff}
          onApprove={enhancedHandleApprove}
          onReject={enhancedHandleReject}
        />
      ) : (
        <BodyContainerEditor
          nodeRoot={_nodeRoot}
          textContent={textContent}
          setTextContent={setTextContent}
        />
      )}
    </div>
  );

  const refAsideLeft = (
    <AsideLeft
      showAside={_showAsideLeft}
      setShowAside={_setShowAsideLeft}
      nodeFunction={_nodeFunction}
      showGlobals={_showGlobals}
      setShowGlobals={_handleShowGlobals}
      showFunction={_showFunction}
      setShowFunction={_handleShowFunction}
      showConditions={_showConditions}
      setShowConditions={_handleShowConditions}
    />
  );

  const refAsideRight = (
    <AsideRight
      showAside={_showAsideRight}
      setShowAside={_setShowAsideRight}
      onAiResponse={enhancedHandleAiResponse}
    />
  );

  const refCenter = (
    <div className={styles["panel"]}>
      {refContainerBody}
      <Footer>
        <CanvasFooterTrash />
      </Footer>
    </div>
  );

  useEffect(() => {
    if (
      nodeUtils.getParameters(_nodeRoot).length === 0 &&
      nodeUtils.getFunctions(_nodeRoot).length === 0
    ) {
      return;
    }

    const _afterCallback = (response) => {
      if (response?.error) {
        return;
      }

      const validation = {};

      const _errors = json5.parse(response)?.["errors"] || [];

      nodeUtils.visitElements(_nodeRoot, (_node) => {
        const _nodeId = nodeUtils.getID(_node);

        for (let i = 0; i < _errors.length; i++) {
          const error = _errors[i];

          if (!error.location) {
            continue;
          }

          const _errorLocation = json5.parse(error["location"]);
          const _frontendId = pathUtils.cnvBackPathToFunFront(_errorLocation);

          if (_nodeId === _frontendId) {
            if (!validation[nodeUtils.getUUID(_node)]) {
              validation[nodeUtils.getUUID(_node)] = [];
            }
            validation[nodeUtils.getUUID(_node)].push({
              isDirect: true,
              code: error.type,
              location: error.location || "",
              message: error.details || "Direct error from backend",
            });

            const _ancestors = nodeUtils.getAncestorNodes(_nodeRoot, _node);

            let _path = nodeUtils.getName(_node);

            _ancestors.forEach((_ancestor) => {
              if (!validation[nodeUtils.getUUID(_ancestor)]) {
                validation[nodeUtils.getUUID(_ancestor)] = [];
              }
              validation[nodeUtils.getUUID(_ancestor)].push({
                isDirect: false,
                name: _path,
                code: error.type,
                location: error.location || "",
                message: error.details || "Direct error from backend",
              });

              _path = `${nodeUtils.getName(_ancestor)}::${_path}`;
            });
          }
        }
      });

      dispatch(setValidation(validation));
    };

    let request = true;
    nodeUtils.visitElements(_nodeRoot, (node) => {
      if (nodeUtils.getName(node) === "") {
        request = false;
      }
    });

    if (request) {
      const modelYaml = exportToYaml(_nodeRoot);

      requestValidation(modelYaml, _endpointTomato, _afterCallback);
    }
  }, [_endpointTomato, _nodeRoot, dispatch]);

  return (
    <>
      {_spinner && <Spinner message={_spinnerMessage} />}
      {_showPageGenerator && <ModalGenerator />}
      <PanelContainer
        refAsideLeft={refAsideLeft}
        refAsideRight={refAsideRight}
        refCenter={refCenter}
        showAsideLeft={_showAsideLeft}
        showAsideRight={_showAsideRight}
      />
    </>
  );
}

const ModalGenerator = () => {
  return (
    <div className={styles["generator__background"]}>
      <div className={styles["generator__foreground"]}>
        <DataGenerationPage />
      </div>
    </div>
  );
};

const PanelContainer = ({
  refAsideLeft,
  refAsideRight,
  refCenter,
  showAsideLeft,
  showAsideRight,
}) => {
  const _dispatch = useDispatch();
  const _isHighlightActive = useSelector(getIsAnyHighlighted);
  const _isContextMenuVisible = useSelector(getIsAnyContextMenuDisplayed);

  const handleOnMouseClick = (e) => {
    e?.stopPropagation();

    if (e.target.id.startsWith("action-")) {
      return;
    }

    if (_isHighlightActive) {
      _dispatch(setHighlight(""));
    }

    if (_isContextMenuVisible) {
      _dispatch(setContextMenu(""));
    }
  };

  if (showAsideLeft >= 0 && showAsideRight >= 0) {
    const paneBody = getPaneAsideCLR();

    const handleOnChangeCLR = (sizes) => {
      setPaneAsideCLR(sizes);
    };

    return (
      <div className={styles["container-vertical"]} onMouseDown={handleOnMouseClick}>
        <SplitPane split="vertical" onChange={handleOnChangeCLR}>
          <Pane minSize="30rem" initialSize={paneBody[0]}>
            {refAsideLeft}
          </Pane>
          <Pane>{refCenter}</Pane>
          <Pane minSize="30rem" initialSize={paneBody[2]}>
            {refAsideRight}
          </Pane>
        </SplitPane>
      </div>
    );
  }

  if (showAsideLeft >= 0 && showAsideRight < 0) {
    const paneBody = getPaneAsideCL();

    const handleOnChangeCL = (sizes) => {
      setPaneAsideCL(sizes);
    };

    return (
      <div className={styles["container-vertical"]} onMouseDown={handleOnMouseClick}>
        <SplitPane split="vertical" onChange={handleOnChangeCL}>
          <Pane minSize="30rem" initialSize={paneBody[0]}>
            {refAsideLeft}
          </Pane>
          <Pane initialSize={paneBody[1]}>{refCenter}</Pane>
        </SplitPane>
        <div className={styles["container-vertical__min"]}> {refAsideRight}</div>
      </div>
    );
  }

  if (showAsideLeft < 0 && showAsideRight >= 0) {
    const paneBody = getPaneAsideCR();

    const handleOnChangeCR = (sizes) => {
      setPaneAsideCR(sizes);
    };

    return (
      <div className={styles["container-vertical"]} onMouseDown={handleOnMouseClick}>
        <div className={styles["container-vertical__min"]}> {refAsideLeft}</div>
        <SplitPane split="vertical" onChange={handleOnChangeCR}>
          <Pane initialSize={paneBody[0]}>{refCenter}</Pane>
          <Pane minSize="30rem" initialSize={paneBody[1]}>
            {refAsideRight}
          </Pane>
        </SplitPane>
      </div>
    );
  }

  return (
    <div className={styles["container-vertical"]} onMouseDown={handleOnMouseClick}>
      <div className={styles["container-vertical__min"]}> {refAsideLeft}</div>
      {refCenter}
      <div className={styles["container-vertical__min"]}>{refAsideRight}</div>
    </div>
  );
};

const BodyContainerEditor = ({ nodeRoot, textContent, setTextContent }) => {
  const monacoRef = useRef(null);
  const editorRef = useRef(null);
  const [isEditorReady, setIsEditorReady] = useState(false);

  // Use the custom hook from AiFunction
  useSyncMonacoWithTextContent(editorRef, textContent, isEditorReady);

  const handleEditorWillMount = (monaco) => {
    monacoRef.current = monaco;

    monaco.editor.defineTheme("default", {
      base: "vs-dark",
      inherit: true,
      rules: [
        {
          token: "*bracket",
          foreground: "#9CDCFE",
          background: "#FFFFFF",
        },
      ],
      colors: {},
    });
  };

  function handleEditorDidMount(editor, monaco) {
    editorRef.current = editor;

    monaco.editor.setTheme("default");
    setIsEditorReady(true);

    if (textContent) {
      editor.getModel().setValue(textContent);
    }
  }

  useEffect(() => {
    if (nodeRoot && (!textContent || textContent === "")) {
      const yamlContent = exportToYaml(nodeRoot);
      setTextContent(yamlContent);
    }
  }, [nodeRoot, textContent, setTextContent]);

  function handleEditorChange(value) {
    setTextContent(value || "");
  }

  return (
    <div className={styles["container-editor"]}>
      <Editor
        defaultLanguage="yaml"
        defaultValue=""
        value={textContent}
        onChange={handleEditorChange}
        onMount={handleEditorDidMount}
        beforeMount={handleEditorWillMount}
        options={{
          automaticLayout: true,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: "on",
          lineNumbers: "on",
          renderWhitespace: "selection",
          folding: true,
          foldingStrategy: "indentation",
        }}
      />
    </div>
  );
};

const BodyHeader = ({ isGui, handleSwitchToGui, handleSwitchToText }) => {
  return (
    <div className={styles["header"]}>
      <div
        className={`${styles["header__button"]} ${
          isGui ? styles["header__button--selected"] : ""
        } `}
        onClick={handleSwitchToGui}>
        Visual
      </div>
      <div
        className={`${styles["header__button"]} ${
          !isGui ? styles["header__button--selected"] : ""
        }`}
        onClick={handleSwitchToText}>
        Text
      </div>
    </div>
  );
};

const BodyContainerGUI = ({
  viewRef,
  functions,
  refGlobals,
  refFunctions,
  refConditions,
  showFunction,
  showConditions,
  showGlobals,
}) => {
  if (functions?.length === 0) {
    return (
      <div ref={viewRef} className={styles["container-horizontal"]}>
        {refGlobals}
      </div>
    );
  }

  if (showGlobals && showFunction && showConditions) {
    const paneBody = getPaneBodyGFC();

    const handleOnChangeGFC = (sizes) => {
      setPaneBodyGFC(sizes);
    };

    return (
      <div ref={viewRef} className={styles["container-horizontal"]}>
        <SplitPane split="horizontal" onChange={handleOnChangeGFC}>
          <Pane minSize="7.5rem" initialSize={paneBody[0]}>
            {refGlobals}
          </Pane>
          <Pane minSize="7.5rem" initialSize={paneBody[1]}>
            {refFunctions}
          </Pane>
          <Pane minSize="7.5rem" initialSize={paneBody[2]}>
            {refConditions}
          </Pane>
        </SplitPane>
      </div>
    );
  }

  if (showGlobals && showFunction && !showConditions) {
    const paneBody = getPaneBodyGF();

    const handleOnChangeGF = (sizes) => {
      setPaneBodyGF(sizes);
    };

    return (
      <div ref={viewRef} className={styles["container-horizontal"]}>
        <SplitPane split="horizontal" onChange={handleOnChangeGF}>
          <Pane minSize="7.5rem" initialSize={paneBody[0]}>
            {refGlobals}
          </Pane>
          <Pane minSize="7.5rem" initialSize={paneBody[1]}>
            {refFunctions}
          </Pane>
        </SplitPane>
        <div className={styles["container-horizontal__min"]}>{refConditions}</div>
      </div>
    );
  }

  if (!showGlobals && showFunction && showConditions) {
    const paneBody = getPaneBodyFC();

    const handleOnChangeFC = (sizes) => {
      setPaneBodyFC(sizes);
    };
    return (
      <div ref={viewRef} className={styles["container-horizontal"]}>
        <div className={styles["container-horizontal__min"]}>{refGlobals}</div>
        <SplitPane split="horizontal" onChange={handleOnChangeFC}>
          <Pane minSize="7.5rem" initialSize={paneBody[0]}>
            {refFunctions}
          </Pane>
          <Pane minSize="7.5rem" initialSize={paneBody[1]}>
            {refConditions}
          </Pane>
        </SplitPane>
      </div>
    );
  }

  if (!showGlobals && showFunction && !showConditions) {
    return (
      <div ref={viewRef} className={styles["container-horizontal"]}>
        <div className={styles["container-horizontal__min"]}>{refGlobals}</div>
        {refFunctions}
        <div className={styles["container-horizontal__min"]}>{refConditions}</div>
      </div>
    );
  }

  return (
    <div ref={viewRef} className={styles["container-horizontal"]}>
      {refGlobals}
      <div className={styles["container__min"]}>{refFunctions}</div>
    </div>
  );
};

const Footer = ({ children }) => {
  return <div className={styles["panel__footer"]}>{children}</div>;
};
